import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { DataCard, DataCardActions, DataCardEmpty, DataCardRow } from '../../components/ui/DataCard';
import { TextField } from '../../components/forms/TextField';
import { cn } from '../../utils/cn';
import { bookingApi, type CustomFormEntity, type ServiceEntity } from '../../api/BookingApi';
import { useI18n } from '../../i18n/I18nContext';

// ---------------------------------------------------------------------------
// Inline helpers
// ---------------------------------------------------------------------------

function Badge({ children, tone }: { children: React.ReactNode; tone: 'gray' | 'green' | 'rose' | 'red' }) {
  const tones: Record<typeof tone, string> = {
    gray: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>
      {children}
    </span>
  );
}

function MultiSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  const selectedLabels = options.filter((o) => value.includes(o.value)).map((o) => o.label);

  return (
    <div ref={ref} className="relative">
      <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-900 shadow-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={cn('truncate', selectedLabels.length === 0 && 'text-slate-400 dark:text-slate-500')}>
          {selectedLabels.length > 0 ? selectedLabels.join(', ') : t('customForms.fields.selectServicesPlaceholder')}
        </span>
        <svg className="ml-2 h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400 dark:text-slate-500">{t('customForms.fields.noServicesAvailable')}</div>
          ) : (
            options.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-600"
                  checked={value.includes(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <span className="text-slate-900 dark:text-slate-100">{o.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type FormDraft = {
  id?: string;
  key: string;
  label: string;
  type: string;
  choices: string[];
  required: boolean;
  applyToAll: boolean;
  serviceIds: string[];
  sequence: number;
  instruction: string;
};

const EMPTY_DRAFT: FormDraft = {
  key: '',
  label: '',
  type: 'input',
  choices: [],
  required: false,
  applyToAll: true,
  serviceIds: [],
  sequence: 0,
  instruction: '',
};

const TYPE_OPTIONS = ['input', 'choices', 'number', 'date', 'dob'] as const;
const DEBOUNCE_MS = 400;

const typeTone = (t: string): 'red' | 'green' | 'rose' | 'gray' => {
  switch (t) {
    case 'input':
      return 'red';
    case 'choices':
      return 'green';
    case 'number':
      return 'rose';
    case 'dob':
      return 'rose';
    case 'date':
      return 'gray';
    default:
      return 'gray';
  }
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function CustomFormsPage() {
  const { t } = useI18n();
  const [forms, setForms] = useState<CustomFormEntity[]>([]);
  const [services, setServices] = useState<ServiceEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<FormDraft>({ ...EMPTY_DRAFT });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Data loading ---

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bookingApi.listCustomForms();
      setForms(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('customForms.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadServices = useCallback(async () => {
    try {
      const res = await bookingApi.listServices();
      setServices(res.data);
    } catch {
      // non-critical — services dropdown will just be empty
    }
  }, []);

  useEffect(() => {
    loadForms();
    loadServices();
  }, [loadForms, loadServices]);

  // --- Client-side filtering & pagination ---

  const filtered = useMemo(() => {
    if (!query.trim()) return forms;
    const q = query.toLowerCase();
    return forms.filter((f) => f.key.toLowerCase().includes(q) || f.label.toLowerCase().includes(q));
  }, [forms, query]);

  const total = filtered.length;
  const page = filtered.slice(skip, skip + limit);

  function handleSearchChange(value: string) {
    setQuery(value);
    setSkip(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {}, DEBOUNCE_MS);
  }

  function handlePageChange(next: { skip: number; limit: number }) {
    setSkip(next.skip);
    setLimit(next.limit);
  }

  function refresh() {
    loadForms();
  }

  // --- Modal logic ---

  function openCreate() {
    setError(null);
    setDraft({ ...EMPTY_DRAFT, sequence: forms.length + 1 });
    setModalOpen(true);
  }

  function openEdit(form: CustomFormEntity) {
    setError(null);
    setDraft({
      id: form.serviceCustomFormId,
      key: form.key,
      label: form.label,
      type: form.type,
      choices: form.choices ?? [],
      required: form.required ?? false,
      applyToAll: form.applyToAll ?? true,
      serviceIds: form.serviceIds ?? [],
      sequence: form.sequence ?? 0,
      instruction: form.instruction ?? '',
    });
    setModalOpen(true);
  }

  async function saveDraft() {
    setError(null);
    const key = draft.key.trim();
    const label = draft.label.trim();
    if (!key) return setError(t('customForms.validation.keyRequired'));
    if (!label) return setError(t('customForms.validation.labelRequired'));
    if (draft.type === 'choices' && draft.choices.filter(Boolean).length === 0)
      return setError(t('customForms.validation.choicesRequired'));

    const payload: Partial<CustomFormEntity> = {
      key,
      label,
      type: draft.type,
      required: draft.required,
      applyToAll: draft.applyToAll,
      serviceIds: draft.applyToAll ? [] : draft.serviceIds,
      sequence: draft.sequence,
      instruction: draft.instruction.trim() || undefined,
      choices: draft.type === 'choices' ? draft.choices.filter(Boolean) : undefined,
    };

    setSaving(true);
    try {
      if (draft.id) {
        await bookingApi.updateCustomForm(draft.id, payload);
      } else {
        await bookingApi.createCustomForm(payload);
      }
      setModalOpen(false);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('customForms.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function removeForm(form: CustomFormEntity) {
    const ok = window.confirm(t('customForms.confirmDelete', { label: form.label }));
    if (!ok) return;
    try {
      await bookingApi.deleteCustomForm(form.serviceCustomFormId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('customForms.deleteFailed'));
    }
  }

  // --- Choice helpers ---

  function addChoice() {
    setDraft((d) => ({ ...d, choices: [...d.choices, ''] }));
  }

  function updateChoice(idx: number, value: string) {
    setDraft((d) => {
      const next = [...d.choices];
      next[idx] = value;
      return { ...d, choices: next };
    });
  }

  function removeChoice(idx: number) {
    setDraft((d) => ({ ...d, choices: d.choices.filter((_, i) => i !== idx) }));
  }

  // --- Service options ---

  const serviceOptions = useMemo(
    () => services.map((s) => ({ value: s.serviceId, label: s.title })),
    [services],
  );

  // --- Render ---

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('customForms.title')}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {t('customForms.subtitle')}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button onClick={openCreate}>{t('customForms.addField')}</Button>
        </div>
      </div>

      {error && !modalOpen ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      {/* Table card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-sm">
            <TextField
              label={t('common.search')}
              placeholder={t('customForms.searchPlaceholder')}
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t('common.total')} <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span>
          </div>
        </div>

        {/* Mobile card list */}
        <div className="mt-4 space-y-3 md:hidden">
          {loading && forms.length === 0 ? (
            <DataCardEmpty>{t('customForms.loadingForms')}</DataCardEmpty>
          ) : page.length === 0 ? (
            <DataCardEmpty>{t('common.noResults')}</DataCardEmpty>
          ) : (
            page.map((f) => (
              <DataCard key={f.serviceCustomFormId}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900 dark:text-slate-100">{f.label}</div>
                    <div className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">{f.key}</div>
                  </div>
                  <Badge tone={typeTone(f.type)}>{t(`customForms.typeOptions.${f.type}`)}</Badge>
                </div>
                <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                  <DataCardRow label={t('customForms.table.required')}>
                    <Badge tone={f.required ? 'green' : 'gray'}>
                      {f.required ? t('common.yes') : t('common.no')}
                    </Badge>
                  </DataCardRow>
                  <DataCardRow label={t('customForms.table.sequence')}>{f.sequence ?? '—'}</DataCardRow>
                </div>
                <DataCardActions>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(f)}>
                    {t('common.edit')}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => removeForm(f)}>
                    {t('common.delete')}
                  </Button>
                </DataCardActions>
              </DataCard>
            ))
          )}
        </div>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 dark:text-slate-400">
              <tr>
                <th className="py-2 pr-4 font-semibold">{t('customForms.table.key')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customForms.table.label')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customForms.table.type')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customForms.table.required')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customForms.table.sequence')}</th>
                <th className="py-2 pr-0 font-semibold text-right">{t('customForms.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && forms.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={6}>
                    {t('customForms.loadingForms')}
                  </td>
                </tr>
              ) : page.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={6}>
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                page.map((f) => (
                  <tr key={f.serviceCustomFormId} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="py-3 pr-4">
                      <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{f.key}</span>
                    </td>
                    <td className="py-3 pr-4 font-medium text-slate-900 dark:text-slate-100">{f.label}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={typeTone(f.type)}>{t(`customForms.typeOptions.${f.type}`)}</Badge>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge tone={f.required ? 'green' : 'gray'}>{f.required ? t('common.yes') : t('common.no')}</Badge>
                    </td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{f.sequence ?? '—'}</td>
                    <td className="py-3 pr-0">
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openEdit(f)}>
                          {t('common.edit')}
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => removeForm(f)}>
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination total={total} limit={limit} skip={skip} onChange={handlePageChange} />
        </div>
      </div>

      {/* Add / Edit modal */}
      <Modal
        open={modalOpen}
        title={draft.id ? t('customForms.editFormField') : t('customForms.addFormField')}
        onClose={() => (saving ? null : setModalOpen(false))}
      >
        <div className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          ) : null}

          <TextField
            label={t('customForms.fields.key')}
            value={draft.key}
            onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value }))}
            placeholder={t('customForms.fields.keyPlaceholder')}
          />

          <TextField
            label={t('customForms.fields.label')}
            value={draft.label}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
            placeholder={t('customForms.fields.labelPlaceholder')}
          />

          <TextField
            label={t('customForms.fields.sequence')}
            type="number"
            value={String(draft.sequence)}
            onChange={(e) => setDraft((d) => ({ ...d, sequence: Number(e.target.value) || 0 }))}
            placeholder="0"
          />

          {/* Type select */}
          <div>
            <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('customForms.fields.type')}</div>
            <div className="flex flex-wrap gap-2">
              {TYPE_OPTIONS.map((typeOpt) => (
                <button
                  key={typeOpt}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, type: typeOpt }))}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                    draft.type === typeOpt
                      ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                  )}
                >
                  {t(`customForms.typeOptions.${typeOpt}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Choices (visible when type === 'choices') */}
          {draft.type === 'choices' && (
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('customForms.fields.choices')}</div>
              <div className="space-y-2">
                {draft.choices.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      value={c}
                      onChange={(e) => updateChoice(i, e.target.value)}
                      placeholder={t('customForms.fields.choicePlaceholder', { n: i + 1 })}
                    />
                    <button
                      type="button"
                      className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:text-slate-500 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                      onClick={() => removeChoice(i)}
                      aria-label={t('common.aria.removeChoice')}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="mt-2 inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                onClick={addChoice}
              >
                {t('customForms.fields.addChoice')}
              </button>
            </div>
          )}

          {/* Required toggle */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-600"
              checked={draft.required}
              onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked }))}
            />
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('customForms.fields.required')}</span>
          </label>

          {/* Apply to All toggle */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-600"
              checked={draft.applyToAll}
              onChange={(e) => setDraft((d) => ({ ...d, applyToAll: e.target.checked }))}
            />
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('customForms.fields.applyToAllServices')}</span>
          </label>

          {/* Service multi-select (when applyToAll is false) */}
          {!draft.applyToAll && (
            <MultiSelect
              label={t('customForms.fields.services')}
              options={serviceOptions}
              value={draft.serviceIds}
              onChange={(ids) => setDraft((d) => ({ ...d, serviceIds: ids }))}
            />
          )}

          {/* Instruction */}
          <label className="block">
            <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('customForms.fields.instruction')}</div>
            <textarea
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
              rows={3}
              value={draft.instruction}
              onChange={(e) => setDraft((d) => ({ ...d, instruction: e.target.value }))}
              placeholder={t('customForms.fields.instructionPlaceholder')}
            />
          </label>

          <div className="flex justify-end gap-2 pt-2 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={saveDraft} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
